// frontend/src/pages/customer/Orders.js
import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiFileText,
  FiPackage,
  FiClock,
  FiCheckCircle,
  FiTruck,
  FiXCircle,
  FiAlertCircle,
  FiEye
} from 'react-icons/fi';

const Orders = () => {
  const { user } = useOutletContext();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, accepted, delivered, cancelled

  useEffect(() => {
    fetchOrders();
  }, [filter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/customer/orders', {
        params: { status: filter !== 'all' ? filter : undefined }
      });

      if (response.data.success) {
        setOrders(response.data.data.orders);
      }
    } catch (error) {
      console.error('Fetch orders error:', error);
      toast.error('Захиалга татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: FiClock, label: 'Хүлээгдэж буй' },
      accepted: { bg: 'bg-blue-100', text: 'text-blue-800', icon: FiCheckCircle, label: 'Баталгаажсан' },
      prepared: { bg: 'bg-purple-100', text: 'text-purple-800', icon: FiPackage, label: 'Бэлтгэгдсэн' },
      dispatched: { bg: 'bg-indigo-100', text: 'text-indigo-800', icon: FiTruck, label: 'Хүргэлтэнд гарсан' },
      delivered: { bg: 'bg-green-100', text: 'text-green-800', icon: FiCheckCircle, label: 'Хүргэгдсэн' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: FiXCircle, label: 'Цуцлагдсан' }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon size={12} />
        {badge.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (order) => {
  // ✅ Хэрэв банкны шилжүүлэг бөгөөд pending_verification бол
  if (order.payment_method === 'bank_transfer' && 
      order.payment_status === 'unpaid' &&
      order.transaction_status === 'pending_verification') {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        Хүлээгдэж байна
      </span>
    );
  }

  const badges = {
    unpaid: { bg: 'bg-red-100', text: 'text-red-800', label: 'Төлөөгүй' },
    partial: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Хэсэгчлэн' },
    paid: { bg: 'bg-green-100', text: 'text-green-800', label: 'Төлсөн' },
    refunded: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Буцаасан' }
  };

  const badge = badges[order.payment_status] || badges.unpaid;

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
      {badge.label}
    </span>
  );
};

  const getPaymentMethodLabel = (method) => {
    const methods = {
      cash: 'Бэлэн',
      card: 'Карт',
      bank_transfer: 'Шилжүүлэг',
      credit: 'Зээл'
    };
    return methods[method] || method;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-lg mb-4"></div>
          <p className="text-gray-600">Захиалга ачаалж байна...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-custom py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <FiFileText size={28} className="text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Миний захиалга</h1>
        </div>
        <p className="text-gray-600">Таны захиалгын түүх болон статус</p>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Бүгд ({orders.length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'pending'
                ? 'bg-yellow-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Хүлээгдэж буй
          </button>
          <button
            onClick={() => setFilter('accepted')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'accepted'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Баталгаажсан
          </button>
          <button
            onClick={() => setFilter('delivered')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'delivered'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Хүргэгдсэн
          </button>
          <button
            onClick={() => setFilter('cancelled')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'cancelled'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Цуцлагдсан
          </button>
        </div>
      </div>

      {/* Orders List */}
      {orders.length === 0 ? (
        <div className="card text-center py-16">
          <FiFileText size={64} className="text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Захиалга олдсонгүй
          </h2>
          <p className="text-gray-600 mb-6">
            {filter === 'all' 
              ? 'Та одоогоор захиалга өгөөгүй байна'
              : 'Энэ төлөвтэй захиалга олдсонгүй'}
          </p>
          <Link to="/customer/products" className="btn btn-primary inline-flex items-center gap-2">
            <FiPackage size={18} />
            <span>Бараа үзэх</span>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => (
            <div key={order.id} className="card hover:shadow-lg transition-shadow">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* Order Info */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        #{order.order_number}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleString('mn-MN', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {getStatusBadge(order.status)}
                      {getPaymentStatusBadge(order)}  {/* ← order бүхэлд нь дамжуулах */}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                    <div>
                      <p className="text-gray-500">Барааны тоо:</p>
                      <p className="font-semibold text-gray-900">
                        {order.total_quantity} ширхэг
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Төлбөрийн хэлбэр:</p>
                      <p className="font-semibold text-gray-900">
                        {getPaymentMethodLabel(order.payment_method)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Нийт дүн:</p>
                      <p className="text-lg font-bold text-blue-600">
                        ₮{order.total_amount.toLocaleString()}
                      </p>
                    </div>
                    <div>
                    <p className="text-gray-500">
                      {order.payment_method === 'bank_transfer' && 
                      order.payment_status === 'unpaid' &&
                      order.transaction_status === 'pending_verification' ? 'Төлсөн:' : 'Төлсөн:'}
                    </p>
                    <p className={`font-semibold ${
                      order.payment_method === 'bank_transfer' && 
                      order.payment_status === 'unpaid' &&
                      order.transaction_status === 'pending_verification'
                        ? 'text-blue-600' 
                        : 'text-green-600'
                    }`}>
                      {order.payment_method === 'bank_transfer' && 
                      order.payment_status === 'unpaid' &&
                      order.transaction_status === 'pending_verification'
                        ? `₮${order.total_amount.toLocaleString()}` 
                        : `₮${order.paid_amount.toLocaleString()}`}
                    </p>
                  </div>
                  </div>

                  {/* Payment Warning */}
                  {order.payment_method === 'bank_transfer' && order.payment_status === 'unpaid' && (
                    order.transaction_status === 'pending_verification' ? (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                        <FiClock className="text-blue-600 mt-0.5 flex-shrink-0" size={16} />
                        <p className="text-sm text-blue-800">
                          Баталгаажуулалт хүлээж байна. Админ таны төлбөрийг шалгаж баталгаажуулах болно.
                        </p>
                      </div>
                    ) : (
                      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                        <FiAlertCircle className="text-yellow-600 mt-0.5 flex-shrink-0" size={16} />
                        <p className="text-sm text-yellow-800">
                          Төлбөр хүлээгдэж байна. Төлбөр шилжүүлсэн бол "Дэлгэрэнгүй" дарж "Төлбөр төлсөн" товч дарна уу.
                        </p>
                      </div>
                    )
                  )}
                </div>

                {/* Actions */}
                <div className="flex lg:flex-col gap-2">
                  <Link
                    to={`/customer/orders/${order.id}`}
                    className="btn btn-primary flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <FiEye size={16} />
                    <span>Дэлгэрэнгүй</span>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination (if needed) */}
      {orders.length > 0 && (
        <div className="mt-6 text-center text-sm text-gray-600">
          Нийт {orders.length} захиалга
        </div>
      )}
    </div>
  );
};

export default Orders;